import { apiClient, useAuthStore } from '@indipia/shared'
import type { ApiResponse } from '@indipia/shared-types'
import type { RecipientType, RecipientWorkType } from '@indipia/shared-features'
import type { ScheduleDetailResponse } from '../types/gantt.types'
import { getShipLedger } from './shipApi'

// ── 알림톡 미리보기 (POST /schedule/shares/preview, HM-02) ─────────────────────
// 문구·버튼은 서버가 Hermes 렌더 결과로 내려준다. 화면은 대상만 보내고 문구를 조립하지 않는다.
// expiresAt 은 서버가 산정한 링크 만료 — 화면은 날짜를 계산하지 않고 이 값만 표시한다.

export type SharePreviewGroup = 'CUSTOMER' | 'GENERAL'

export interface SharePreviewRecipient {
  name: string
  recipientType: RecipientType
  workTypeCode?: string
  partnerId?: number
}

export interface SharePreviewRequest {
  shareType: 'SCHEDULE_SHARE'
  siteId: number
  group: SharePreviewGroup
  recipient: SharePreviewRecipient
}

export interface SharePreviewButton { type: string; name: string; url: string | null }

export interface SharePreviewResponse {
  group: SharePreviewGroup
  title: string | null
  body: string
  buttons: SharePreviewButton[]
  /** 서버 산정 링크 만료(ISO 날짜시각) */
  expiresAt: string | null
  failoverBody: string | null
}

export async function previewScheduleShare(req: SharePreviewRequest, signal?: AbortSignal): Promise<SharePreviewResponse> {
  const res = await apiClient.post<ApiResponse<SharePreviewResponse>>('/schedule/shares/preview', req, { signal })
  return res.data.data
}

/** 서버 오류 응답의 메시지 — 없으면 일반 문구 */
export function previewErrorMessage(err: unknown): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
  return msg || '미리보기를 불러오지 못했습니다.'
}

// ── 수신자 담당 공종 (기존 스케줄 상세·발주 원장 API 재사용) ───────────────────
// 반장: GET /schedule/schedules/{id} 의 tasks 중 assigneePartnerId 가 같은 공정의 공종.
// 거래처: GET /schedule/schedules/{id}/order-vendors 원장 행의 taskId → 스케줄 상세 tasks 의 공종.
// 같은 공종코드 공정이 여럿이어도 공종코드 하나로 묶는다(확정 결정 49).
export async function fetchRecipientWorkTypes(
  scheduleId: number,
  recipientType: RecipientType,
  recipientId: number,
): Promise<RecipientWorkType[]> {
  const user = useAuthStore.getState().user
  const headers = {
    ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }
  const detail = await apiClient.get<ApiResponse<ScheduleDetailResponse>>(`/schedule/schedules/${scheduleId}`, { headers })
  const tasks = detail.data.data?.tasks ?? []

  let taskIds: Set<number>
  if (recipientType === 'PARTNER') {
    taskIds = new Set(tasks.filter(t => t.assigneePartnerId === recipientId).map(t => t.taskId))
  } else {
    const ledger = await getShipLedger(scheduleId)
    taskIds = new Set(ledger.filter(r => r.vendorId === recipientId).map(r => r.taskId))
  }

  const seen = new Map<string, RecipientWorkType>()
  for (const t of [...tasks].sort((a, b) => a.displayOrder - b.displayOrder)) {
    if (!taskIds.has(t.taskId) || !t.workTypeCode || seen.has(t.workTypeCode)) continue
    seen.set(t.workTypeCode, { code: t.workTypeCode, name: t.workTypeName || t.workTypeCode })
  }
  return [...seen.values()]
}

// ── 공유 이력: 회수 영향·재발송·발송 가능 여부 ─────────────────────────────────

function authHeaders() {
  const user = useAuthStore.getState().user
  return {
    ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }
}

/** 서버 오류 응답의 { status, code, message } — 없으면 빈 값 */
export function apiErrorInfo(err: unknown): { status?: number; code?: string; message?: string } {
  const r = (err as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response
  return { status: r?.status, code: r?.data?.code, message: r?.data?.message }
}

/** 종료일+7일 경과로 서버가 알림톡 발송을 거부한 오류(422) 코드 */
export const SHARE_SEND_WINDOW_CLOSED = 'SHARE_SEND_WINDOW_CLOSED'

/** 종료일+7일 경과 안내(이력 툴팁 공통 문구) — 버튼 이름은 쓰지 않는다 */
export const SEND_WINDOW_CLOSED_NOTICE = '공사 종료일에서 7일이 지나 알림톡은 보낼 수 없어요. 링크를 복사해 직접 보내 주세요.'

export interface RevokeImpactMenu { menu: string; historyCount: number }

/** GET /schedule/schedules/{scheduleId}/share-history/{historyId}/revoke-impact (PL-13, 정보 제공만) */
export interface RevokeImpact {
  shareKey: string | null
  shareId: number | null
  linkActive: boolean
  sameLinkHistoryCount: number
  viewCount: number
  lastViewedDtm: string | null
  otherMenus: RevokeImpactMenu[]
}

export async function fetchRevokeImpact(scheduleId: number, historyId: number, signal?: AbortSignal): Promise<RevokeImpact> {
  const res = await apiClient.get<ApiResponse<RevokeImpact>>(
    `/schedule/schedules/${scheduleId}/share-history/${historyId}/revoke-impact`,
    { headers: authHeaders(), signal },
  )
  return res.data.data
}

/** 서버 재발송 응답 — 발송 응답(ScheduleShareSendResponse)과 같은 모양으로 가정(건수 필드는 없을 수 있음) */
export interface ShareResendResult {
  sentCount?: number
  failedCount?: number
}

/** 이력 번호 기반 서버 재발송(HS-04) — 같은 링크·새 이력 행·본문과 이력은 서버가 처리한다. 경로는 회수와 같은 계열. */
export async function resendScheduleShareHistory(scheduleId: number, historyId: number): Promise<ShareResendResult> {
  const res = await apiClient.post<ApiResponse<ShareResendResult>>(
    `/schedule/schedules/${scheduleId}/share-history/${historyId}/resend`,
    {},
    { headers: authHeaders() },
  )
  return res.data.data ?? {}
}

/**
 * 스케줄 번호별 알림톡 발송 가능 여부 — GET /schedule/sites 의 shareSendable(종료일+7일 판정은 서버 한 곳).
 * 화면은 날짜를 계산하지 않고 이 값만 쓴다. 스케줄이 없는 현장은 제외.
 */
export async function fetchShareSendableBySchedule(signal?: AbortSignal): Promise<Map<number, boolean>> {
  const res = await apiClient.get<ApiResponse<{ items: { scheduleId: number | null; shareSendable?: boolean }[] }>>(
    '/schedule/sites',
    { headers: authHeaders(), signal },
  )
  const map = new Map<number, boolean>()
  for (const it of res.data.data?.items ?? []) {
    if (it.scheduleId != null && typeof it.shareSendable === 'boolean') map.set(it.scheduleId, it.shareSendable)
  }
  return map
}
